import { ArrowIcon } from "@/assets/images/svgIcons/arrowIcon"
import { Badge } from "@/components/ui/baseUi/badge"
import { Button } from "@/components/ui/baseUi/button"
import { Separator } from "@/components/ui/baseUi/separator"
import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { useCurrentBox } from "@/modules/box/hooks/useBox"
import { BoxStaffPermissions } from "@/modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@/modules/boxStaff/hooks/useBoxStaffPermissions"
import { word } from "@/modules/core/utils/i18n"
import { ResumeCardPriceDisplay } from "@/modules/finances/components/resumeCardPriceDisplay"
import { useFinanceStats } from "@/modules/finances/hooks/useFinance"
import { usePeriodParam } from "@/modules/finances/hooks/usePeriod"
import { createTransactionsResumeCardModel } from "@/modules/finances/models/transactionsResumeCardModel"
import { createPaymentsToValidateSummaryModel } from "@/modules/finances/paymentsToValidate/models/paymentsToValidateModel"
import { cn } from "@/modules/lib/utils"
import { useBoxNavigate } from "@/modules/navigation/hooks/useBoxNavigate"
import { useMySpaceNavigate } from "@/modules/navigation/hooks/useMySpaceNavigate"
import { Path } from "@/modules/navigation/routes"
import { LoadingSpinner } from "@/modules/ui/components/loading"
import { CircleQuestionMarkIcon } from "lucide-react"

type TransactionsResumeCardProps = {
  className?: string
} & React.ComponentProps<"div">

export function TransactionsResumeCard({ className, ...props }: TransactionsResumeCardProps) {
  const boxNavigate = useBoxNavigate()
  const mySpaceNavigate = useMySpaceNavigate()
  const [period] = usePeriodParam()
  const { result: box } = useCurrentBox()

  const canSeeFinancial = useBoxStaffPermission(BoxStaffPermissions.SEE_FINANCIAL)
  const { data: stats, isLoading } = useFinanceStats(canSeeFinancial || !box, period)

  if (isLoading || !stats) return <LoadingSpinner />

  const { monthlyRevenue, monthlyRevenueAtCounter, monthlyRevenueFees, monthlyRevenueRefunds, monthlyRevenueSepa } =
    createTransactionsResumeCardModel(stats)
  const { paymentToValidate, paymentFailed, paymentDisputed } = createPaymentsToValidateSummaryModel(stats, false)

  const handleViewAll = () => {
    if (box?.id) {
      boxNavigate(`${Path.Box.Finances.Root}/${Path.Box.Finances.PaymentsToValidate}`)
    } else {
      mySpaceNavigate(`${Path.MySpace.Finances.Root}/${Path.MySpace.Finances.PaymentsToValidate}`)
    }
  }

  return (
    <div
      className={cn(
        "flex max-w-140 flex-row items-start gap-3 rounded-4xl bg-linear-to-r from-interactive-background-primary-gradient-hover-from to-interactive-background-primary-gradient-hover-to px-3 py-2 md:gap-6 md:px-8 md:py-6",
        className,
      )}
      {...props}
    >
      <div className="flex flex-4/9 flex-col gap-3 overflow-hidden">
        {/* MONTHLY REVENUE TOTAL */}
        <div className="flex flex-col gap-1">
          <ResumeCardPriceDisplay
            price={{ value: monthlyRevenue, currency: stats.stats.currency }}
            wholePartFontStyle="HM"
            decimalPartFontStyle="TL"
            wholePartClassName="text-textOnSurface-subtle"
            decimalPartClassName="text-textOnSurface-subtle"
          />
          <div className="flex flex-row items-center gap-2">
            <Text fontStyle="BS" className="text-textOnSurface-subtle">
              {word("finances.page.transactions.resume.monthlyRevenue")}
            </Text>
            <Tooltip>
              <TooltipTrigger
                render={<CircleQuestionMarkIcon color="currentColor" className="size-4 text-textOnSurface-subtle" />}
              />
              <TooltipContent side="bottom" className="max-w-120 wrap-break-word whitespace-break-spaces">
                <Text fontStyle="BM">
                  {box
                    ? word("finances.page.transactions.resume.monthlyRevenueTooltip")
                    : word("finances.page.transactions.resume.monthlyRevenueTooltipForCoach")}
                </Text>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* MONTHLY REVENUE DETAILS */}
        <div className="flex flex-col gap-2">
          {/* MONTHLY REVENUE DETAILS - INCLUDING */}
          {box ? (
            <div className="flex flex-col gap-1">
              <Text fontStyle="LXS" className="text-textOnSurface-muted">
                {word("finances.page.transactions.resume.monthlyRevenueDetails.including")}
              </Text>
              <Separator className="bg-surface-border-primary-default" />
              <div className="flex flex-row items-center justify-between">
                <Text fontStyle="LM" className="text-textOnSurface-subtle">
                  {word("finances.page.transactions.resume.monthlyRevenueDetails.at_counter")}
                </Text>
                <ResumeCardPriceDisplay
                  price={{ value: monthlyRevenueAtCounter, currency: stats.stats.currency }}
                  wholePartFontStyle="LM"
                  decimalPartFontStyle="LM"
                  wholePartClassName="text-textOnSurface-subtle"
                  decimalPartClassName="text-textOnSurface-subtle"
                />
              </div>
            </div>
          ) : null}

          {/* MONTHLY REVENUE DETAILS - EXCLUDING */}
          <div className="flex flex-col gap-1">
            <Text fontStyle="LXS" className="text-textOnSurface-muted">
              {word("finances.page.transactions.resume.monthlyRevenueDetails.excluding")}
            </Text>
            <Separator className="bg-surface-border-primary-default" />

            {/* MONTHLY REVENUE DETAILS - FEES */}
            <div className="flex flex-row items-center justify-between">
              <Text fontStyle="LM" className="text-textOnSurface-subtle">
                {word("finances.page.transactions.resume.monthlyRevenueDetails.fees")}
              </Text>
              <Text fontStyle="LM" className="text-textOnSurface-subtle">
                -
                <ResumeCardPriceDisplay
                  price={{ value: monthlyRevenueFees, currency: stats.stats.currency }}
                  wholePartFontStyle="LM"
                  decimalPartFontStyle="LM"
                  wholePartClassName="text-textOnSurface-subtle"
                  decimalPartClassName="text-textOnSurface-subtle"
                />
              </Text>
            </div>

            {/* MONTHLY REVENUE DETAILS - REFUNDS */}
            <div className="flex flex-row items-center justify-between">
              <Text fontStyle="LM" className="text-textOnSurface-subtle">
                {word("finances.page.transactions.resume.monthlyRevenueDetails.refunds")}
              </Text>
              <Text fontStyle="LM" className="text-textOnSurface-subtle">
                -
                <ResumeCardPriceDisplay
                  price={{ value: monthlyRevenueRefunds, currency: stats.stats.currency }}
                  wholePartFontStyle="LM"
                  decimalPartFontStyle="LM"
                  wholePartClassName="text-textOnSurface-subtle"
                  decimalPartClassName="text-textOnSurface-subtle"
                />
              </Text>
            </div>

            {/* MONTHLY REVENUE DETAILS - SEPA */}
            {box ? (
              <div className="flex flex-row items-center justify-between">
                <Text fontStyle="LM" className="text-textOnSurface-subtle">
                  {word("finances.page.transactions.resume.monthlyRevenueDetails.sepa")}
                  <Text fontStyle="LXS" className="ml-1 text-textOnSurface-subtle">
                    {word("finances.page.transactions.resume.monthlyRevenueDetails.sepaPending")}
                  </Text>
                </Text>
                <Text fontStyle="LM" className="text-textOnSurface-subtle">
                  +
                  <ResumeCardPriceDisplay
                    price={{ value: monthlyRevenueSepa, currency: stats.stats.currency }}
                    wholePartFontStyle="LM"
                    decimalPartFontStyle="LM"
                    wholePartClassName="text-textOnSurface-subtle"
                    decimalPartClassName="text-textOnSurface-subtle"
                  />
                </Text>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <Separator orientation="vertical" className="h-full bg-surface-border-primary-default" />

      {/* MONTHLY REVENUE ACTIONS */}
      <div className="flex h-full flex-4/9 flex-col justify-between gap-2 overflow-hidden">
        <div className="flex flex-row flex-wrap items-stretch justify-between gap-3">
          <div className="flex flex-col">
            <ResumeCardPriceDisplay
              price={{ value: paymentToValidate.total, currency: stats.stats.currency }}
              wholePartFontStyle="TL"
              decimalPartFontStyle="TS"
              wholePartClassName="text-textOnSurface-subtle"
              decimalPartClassName="text-textOnSurface-subtle"
            />
            <Badge variant={paymentToValidate.badgeVariant} size="lg">
              {`${paymentToValidate.count.toString()} ${word("finances.page.paymentToValidate.summaryCard.actionRequired", { count: paymentToValidate.count })}`}
            </Badge>
          </div>
          <div className="flex flex-col">
            <ResumeCardPriceDisplay
              price={{ value: paymentFailed.total, currency: stats.stats.currency }}
              wholePartFontStyle="TL"
              decimalPartFontStyle="TS"
              wholePartClassName="text-textOnSurface-subtle"
              decimalPartClassName="text-textOnSurface-subtle"
            />
            <Badge variant={paymentFailed.badgeVariant} size="lg">
              {`${paymentFailed.count.toString()} ${word("finances.page.paymentToValidate.summaryCard.failed", { count: paymentFailed.count })}`}
            </Badge>
          </div>
          <div className="flex flex-col">
            <ResumeCardPriceDisplay
              price={{ value: paymentDisputed.total, currency: stats.stats.currency }}
              wholePartFontStyle="TL"
              decimalPartFontStyle="TS"
              wholePartClassName="text-textOnSurface-subtle"
              decimalPartClassName="text-textOnSurface-subtle"
            />
            <Badge variant={paymentDisputed.badgeVariant} size="lg">
              {`${paymentDisputed.count.toString()} ${word("finances.page.paymentToValidate.summaryCard.disputed", { count: paymentDisputed.count })}`}
            </Badge>
          </div>
        </div>

        <Button variant="underline" size="lg" onClick={() => void handleViewAll()} className="self-end">
          {word("finances.page.transactions.resume.monthlyRevenueActions.toValidate")}
          <ArrowIcon arrowDirection="right" color="currentColor" className="size-4" />
        </Button>
      </div>
    </div>
  )
}
